import { Box, Stack, Typography } from '@mui/material';
import { noop } from 'lodash';

import { Card } from '@/components/layout/Card/Card';
import { LoadingOverlay } from '@/components/progress/LoadingOverlay/LoadingOverlay';
import { TextEditorProvider } from '@/components/TextEditor/contexts/TextEditor.provider';
import { TextEditor } from '@/components/TextEditor/TextEditor';
import { DocumentType } from '@/types/schema';

import { DocumentSelectorRow } from './DocumentSelectorRow';

interface PreviewPanelProps {
  selectedDocumentId: string;
  documentType: DocumentType;
  onSelectDocument: (_event: unknown, value: string | null) => void;
  onGeneratePreview: () => void;
  previewMarkdown: string | null;
  previewResetKey: number;
  isGenerating: boolean;
  isDisabled: boolean;
}

export function PreviewPanel({
  selectedDocumentId,
  documentType,
  onSelectDocument,
  onGeneratePreview,
  previewMarkdown,
  previewResetKey,
  isGenerating,
  isDisabled,
}: PreviewPanelProps) {
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <DocumentSelectorRow
        selectedDocumentId={selectedDocumentId}
        documentType={documentType}
        onSelectDocument={onSelectDocument}
        onGeneratePreview={onGeneratePreview}
        isDisabled={isDisabled}
      />

      <Stack
        sx={{
          flex: 1,
          px: 2,
          py: 1,
          minHeight: 0,
          position: 'relative',
        }}
      >
        {!selectedDocumentId ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body-xs">Select a document to test</Typography>
          </Box>
        ) : (
          <TextEditorProvider theme="userGeneratedContent">
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <TextEditor
                key={`preview-${selectedDocumentId}-${previewResetKey}`}
                initialValue={previewMarkdown ?? ''}
                initialValueKind="markdown"
                readOnly
                hideBorder
                onUpdate={noop} // no updates are needed, but onUpdate is required by the TextEditor component
              />
            </Box>
          </TextEditorProvider>
        )}

        <LoadingOverlay
          open={isGenerating}
          updatingText="Generating preview (1-2 minutes)..."
        />
      </Stack>
    </Card>
  );
}
